import type { Catalog, UiLocale } from './locales.js';
import { LOCALE_TAG } from './locales.js';

/** Values substituted into `{placeholder}` slots. */
export type Vars = Record<string, string | number>;

const pluralRules = new Map<UiLocale, Intl.PluralRules>();

/**
 * The CLDR plural category for `count` in `locale` — `one`, `few`, `many`…
 *
 * Delegated to the platform because the rules are genuinely hard: Polish and
 * Ukrainian pick between four forms on the last TWO digits, so 22 is `few`
 * ("22 klienci") while 25 is `many` ("25 klientów") and 5 is `many` but 2 is
 * `few`. Hand-rolling that is how translation bugs ship.
 */
export function pluralCategory(locale: UiLocale, count: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(LOCALE_TAG[locale]);
    pluralRules.set(locale, rules);
  }
  return rules.select(count);
}

/**
 * Resolve a key to its raw (uninterpolated) string.
 *
 * The fallback chain never dead-ends at a blank (doc 20 R9): a plural form the
 * locale doesn't define falls back to the bare key, a key the locale hasn't
 * translated falls back to ENGLISH, and only a key that exists nowhere renders
 * as itself — which is loud enough to catch in review but still leaves a usable
 * screen for the owner.
 */
export function lookup(
  catalog: Catalog,
  fallback: Catalog,
  locale: UiLocale,
  key: string,
  count?: number,
): string {
  if (count !== undefined) {
    const suffixed = `${key}_${pluralCategory(locale, count)}`;
    const plural = catalog[suffixed] ?? catalog[`${key}_other`];
    if (plural !== undefined) return plural;
    const englishPlural = fallback[suffixed] ?? fallback[`${key}_other`];
    if (englishPlural !== undefined) return englishPlural;
  }
  return catalog[key] ?? fallback[key] ?? key;
}

/** Substitute `{name}` slots. An unknown slot is left verbatim, so a broken
 *  translation shows `{count}` rather than swallowing the sentence. */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/** Split a template into its literal and `{placeholder}` parts, in order —
 *  the basis for interpolating React nodes without concatenating sentences. */
export function splitTemplate(template: string): ({ text: string } | { slot: string })[] {
  const parts: ({ text: string } | { slot: string })[] = [];
  let last = 0;
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    const at = match.index;
    if (at > last) parts.push({ text: template.slice(last, at) });
    parts.push({ slot: match[1]! });
    last = at + match[0].length;
  }
  if (last < template.length) parts.push({ text: template.slice(last) });
  return parts;
}
